import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, ReverCard, toast } from '@itsrever/design-system'
import {
  DayCountField,
  MAX_REVIEW_AUTO_COMPLETE_DAYS,
  ReturnConditionsSettings,
  ReviewAutoCompleteMode,
  changedReturnConditionsFields,
  dayCountErrorCopy,
  invalidReturnConditionsFields
} from '@/application/manage-return-conditions'
import { SaveBar } from '@/ui/shared'

interface ReturnConditionsFormProps {
  canEdit: boolean
  returnConditions: ReturnConditionsSettings
  originalReturnConditions: ReturnConditionsSettings
  setReturnConditions: (conditions: ReturnConditionsSettings) => void
  saveReturnConditions: (
    original: ReturnConditionsSettings,
    edited: ReturnConditionsSettings
  ) => void
  loadingSaveReturnConditions: boolean
}

export const ReturnConditionsForm: React.FC<ReturnConditionsFormProps> = ({
  canEdit,
  returnConditions,
  originalReturnConditions,
  setReturnConditions,
  saveReturnConditions,
  loadingSaveReturnConditions
}) => {
  const { t } = useTranslation()

  const invalidFields = invalidReturnConditionsFields(
    originalReturnConditions,
    returnConditions
  )
  // The auto-complete mode and its days are one setting to the merchant.
  const changes = new Set(
    changedReturnConditionsFields(
      originalReturnConditions,
      returnConditions
    ).map((field) =>
      field === 'reviewAutoCompleteDays' ? 'reviewAutoComplete' : field
    )
  ).size

  // Only the return window is required: the server refuses a non-positive value for it on
  // every write, while an unset shipping deadline is valid and is what most e-commerces have.
  const fields: {
    name: DayCountField
    copyKey: string
    required: boolean
  }[] = [
    { name: 'returnPeriodDays', copyKey: 'return_window', required: true },
    {
      name: 'maxDaysToSendPackage',
      copyKey: 'days_to_ship_package',
      required: false
    }
  ]

  const { reviewAutoComplete, manualReview } = returnConditions
  const autoCompleteEditable = canEdit && manualReview
  const daysInputId = useId()
  const daysInvalid = invalidFields.includes('reviewAutoCompleteDays')
  // Saved days sit inline next to the options; touching the setting opens them in a panel that
  // stays open until save or discard, so typing past the saved value (10 on the way to 100)
  // does not close it under the cursor.
  const [autoCompleteTouched, setAutoCompleteTouched] = useState(false)
  useEffect(() => setAutoCompleteTouched(false), [originalReturnConditions])
  const showDaysPanel =
    reviewAutoComplete !== 'OFF' && (autoCompleteTouched || daysInvalid)
  const daysInputProps = {
    type: 'number',
    inputMode: 'numeric',
    min: 1,
    max: MAX_REVIEW_AUTO_COMPLETE_DAYS,
    step: 1,
    required: true,
    disabled: !autoCompleteEditable,
    value: returnConditions.reviewAutoCompleteDays ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setAutoCompleteTouched(true)
      handleChange('reviewAutoCompleteDays', e.currentTarget.value)
    }
  } as const

  const handleChange = (field: DayCountField, value: string) => {
    setReturnConditions({ ...returnConditions, [field]: toDayCount(value) })
  }

  // Off holds no days, so going back to Off undoes a days edit instead of saving it.
  const handleModeChange = (mode: ReviewAutoCompleteMode) => {
    setAutoCompleteTouched(true)
    setReturnConditions({
      ...returnConditions,
      reviewAutoComplete: mode,
      reviewAutoCompleteDays:
        mode === 'OFF' ? null : returnConditions.reviewAutoCompleteDays
    })
  }

  const handleClickDiscard = () => {
    setAutoCompleteTouched(false)
    setReturnConditions(originalReturnConditions)
  }

  const errorCopyFor = (field: DayCountField, days: number | null) => {
    const { key, max } = dayCountErrorCopy(field, days)
    return t(key, { max })
  }

  const handleClickSave = () => {
    if (invalidFields.length > 0) {
      toast({ text: t('return_conditions.warn_toast'), variant: 'warning' })
      return
    }
    saveReturnConditions(originalReturnConditions, returnConditions)
  }

  return (
    <>
      <ReverCard className="p-4" fullWidth>
        <div className="flex flex-col gap-6">
          {fields.map(({ name, copyKey, required }) => {
            const invalid = invalidFields.includes(name)
            return (
              <Base.FieldRoot key={name} invalid={invalid}>
                <Base.FieldLabel>
                  {t(`return_conditions.${copyKey}`)}
                  {required ? (
                    <span aria-hidden="true" className="text-red-500">
                      {' '}
                      *
                    </span>
                  ) : null}
                </Base.FieldLabel>
                <Base.FieldDescription>
                  {t(`return_conditions.${copyKey}_description`)}
                </Base.FieldDescription>
                <Base.Input
                  className={HIDE_NUMBER_SPINNER}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  required={required}
                  disabled={!canEdit}
                  value={returnConditions[name] ?? ''}
                  onChange={(e) => handleChange(name, e.currentTarget.value)}
                />
                {invalid ? (
                  <Base.FieldError match>
                    {errorCopyFor(name, returnConditions[name])}
                  </Base.FieldError>
                ) : null}
              </Base.FieldRoot>
            )
          })}
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-neutral-900">
              {t('return_conditions.auto_complete')}
            </p>
            <p className="text-xs text-neutral-500">
              {manualReview
                ? t('return_conditions.auto_complete_description')
                : t('return_conditions.auto_complete_auto_review')}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Base.ToggleGroup
                variant="button"
                aria-label={t('return_conditions.auto_complete')}
                value={[reviewAutoComplete]}
                // Single choice: pressing the selected option again keeps it selected.
                onValueChange={(next) =>
                  next.length > 0 &&
                  handleModeChange(next[0] as ReviewAutoCompleteMode)
                }
                disabled={!autoCompleteEditable}
              >
                {AUTO_COMPLETE_MODES.map((mode) => (
                  <Base.ToggleGroupItem key={mode} value={mode}>
                    {t(`return_conditions.auto_complete_${mode.toLowerCase()}`)}
                  </Base.ToggleGroupItem>
                ))}
              </Base.ToggleGroup>
              {reviewAutoComplete !== 'OFF' && !showDaysPanel ? (
                <span className="ml-1 flex items-center gap-1.5 border-0 border-l border-solid border-neutral-200 pl-3 text-xs text-neutral-500">
                  <label
                    htmlFor={daysInputId}
                    className="text-xs text-neutral-500"
                  >
                    {t('return_conditions.auto_complete_days')}
                  </label>
                  <Base.Input
                    id={daysInputId}
                    size="sm"
                    className={`w-16 ${HIDE_NUMBER_SPINNER}`}
                    {...daysInputProps}
                  />
                  {t('return_conditions.auto_complete_days_unit')}
                </span>
              ) : null}
            </div>
            {reviewAutoComplete === 'OFF' ? null : (
              <p className="text-xs text-neutral-500">
                {t(
                  `return_conditions.auto_complete_${reviewAutoComplete.toLowerCase()}_description`
                )}
              </p>
            )}
            {showDaysPanel ? (
              <Base.Card variant="muted" className="mt-3 p-3">
                <Base.FieldRoot invalid={daysInvalid}>
                  <Base.FieldLabel>
                    {t('return_conditions.auto_complete_days')}
                  </Base.FieldLabel>
                  <Base.FieldDescription>
                    {t('return_conditions.auto_complete_days_hint')}
                  </Base.FieldDescription>
                  <span className="mt-1 flex items-center gap-2 text-xs text-neutral-700">
                    <Base.Input
                      autoFocus
                      className={`w-20 ${HIDE_NUMBER_SPINNER}`}
                      {...daysInputProps}
                    />
                    {t('return_conditions.auto_complete_days_unit')}
                  </span>
                  {daysInvalid ? (
                    <Base.FieldError match>
                      {errorCopyFor(
                        'reviewAutoCompleteDays',
                        returnConditions.reviewAutoCompleteDays
                      )}
                    </Base.FieldError>
                  ) : null}
                </Base.FieldRoot>
              </Base.Card>
            ) : null}
          </div>
        </div>
      </ReverCard>
      <SaveBar
        editable={canEdit}
        changes={changes}
        saving={loadingSaveReturnConditions}
        onDiscard={handleClickDiscard}
        onSave={handleClickSave}
      />
    </>
  )
}

const AUTO_COMPLETE_MODES: ReviewAutoCompleteMode[] = [
  'OFF',
  'MISSING',
  'APPROVED'
]

// Base.Input puts className on its wrapper, so the spinner is hidden on the inner input.
const HIDE_NUMBER_SPINNER =
  '[&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none [&_input]:[appearance:textfield]'

function toDayCount(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  return Number.isNaN(parsed) ? null : parsed
}
